import { create } from "zustand";

// Pending agreement interface
interface PendingAgreement {
  name: string;
  version: string;
}

// Agreements Store Interface
interface AgreementsStore {
  // Pending agreements queue
  pendingAgreements: PendingAgreement[];
  currentAgreementIndex: number;

  // UI State - Confirmation Modal (post-login)
  isAgreementModalOpen: boolean;

  // UI State - View Modal (read-only, pre-registration)
  isViewModalOpen: boolean;
  viewAgreementName: string | null;

  // Actions
  setPendingAgreements: (agreements: PendingAgreement[]) => void;
  nextAgreement: () => void;
  openAgreementModal: () => void;
  closeAgreementModal: () => void;
  openViewModal: (name: string) => void;
  closeViewModal: () => void;
  reset: () => void;
}

const initialState = {
  pendingAgreements: [],
  currentAgreementIndex: 0,
  isAgreementModalOpen: false,
  isViewModalOpen: false,
  viewAgreementName: null,
};

export const useAgreementsStore = create<AgreementsStore>((set, get) => ({
  ...initialState,

  // Set pending agreements and open modal if there are any
  setPendingAgreements: (agreements: PendingAgreement[]) => {
    set({
      pendingAgreements: agreements,
      currentAgreementIndex: 0,
      isAgreementModalOpen: agreements.length > 0,
    });
  },

  // Move to next agreement or close modal if done
  nextAgreement: () => {
    const { currentAgreementIndex, pendingAgreements } = get();
    const nextIndex = currentAgreementIndex + 1;

    if (nextIndex < pendingAgreements.length) {
      // Move to next agreement
      set({ currentAgreementIndex: nextIndex });
    } else {
      // All agreements confirmed, close modal and reset
      set({
        isAgreementModalOpen: false,
        pendingAgreements: [],
        currentAgreementIndex: 0,
      });
    }
  },

  // Open agreement modal
  openAgreementModal: () => {
    set({ isAgreementModalOpen: true });
  },

  // Close agreement modal
  closeAgreementModal: () => {
    set({ isAgreementModalOpen: false });
  },

  // Open view-only modal for a specific agreement
  openViewModal: (name: string) => {
    set({ isViewModalOpen: true, viewAgreementName: name });
  },

  // Close view-only modal
  closeViewModal: () => {
    set({ isViewModalOpen: false, viewAgreementName: null });
  },

  // Reset store to initial state
  reset: () => {
    set(initialState);
  },
}));

